import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { getUserLocalLogin, type CurrentUser } from "@/lib/auth";
import type { Order } from "@/lib/projects";
import {
  deleteOrderNote,
  postOrderNote,
  type ProjectNote,
} from "@/lib/projectOverview";
import { NoteComposer } from "@/projectOverview/notes/NoteComposer";
import { NoteMessageBody } from "@/projectOverview/notes/NoteMessageBody";
import { SystemNoteCard } from "@/projectOverview/notes/SystemNoteCard";
import { parseSystemNote } from "@/projectOverview/notes/systemNote";
import {
  buildEmployeeMentionSeed,
  resolveReplyEmployee,
  type MentionSeed,
} from "@/projectOverview/notes/replyMention";
import { useNoteReferenceAction } from "@/projectOverview/notes/useNoteReferenceAction";
import type { DraftNoteReference } from "@/projectOverview/notes/types";
import type { ProjectOverviewReplyDraft } from "@/projectOverview/projectOverviewContextValue";
import { OverviewCard } from "@/projectOverview/OverviewCard";
import { notifyMentionsBadgeRefresh } from "@/lib/navBadges";

function fmtNoteTime(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface DisplayNote extends ProjectNote {
  _system?: boolean;
  _pending?: boolean;
}

export function NotesSection({
  orderId,
  project,
  notes,
  user,
  isBuero,
  onChanged,
  allowCompose = true,
  highlightNoteId = null,
  replyDraft = null,
}: {
  orderId: number;
  project: Order;
  notes: ProjectNote[];
  user: CurrentUser | null;
  isBuero: boolean;
  onChanged: () => void;
  /** False = nur Lesen (kein Composer). */
  allowCompose?: boolean;
  highlightNoteId?: number | null;
  /** Antwort auf eine Erwähnung: Absender schon im Textfeld erwähnen. */
  replyDraft?: ProjectOverviewReplyDraft | null;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mentionSeed, setMentionSeed] = useState<MentionSeed | null>(null);
  const [replyHint, setReplyHint] = useState<string | null>(null);
  const highlightRef = useRef<HTMLElement | null>(null);
  const { handleRefClick, fallbackModal } = useNoteReferenceAction(orderId);

  const login = getUserLocalLogin(user);

  const displayNotes = useMemo<DisplayNote[]>(() => {
    const list: DisplayNote[] = [...notes];
    if (project.created_at) {
      const createdBy = project.created_by_name || project.created_by || "Unbekannt";
      list.unshift({
        id: "system-created",
        note: `Angelegt von ${createdBy} · ${fmtDateTime(project.created_at)}`,
        created_by_display: "System",
        created_at: project.created_at,
        _system: true,
      });
    }
    return list;
  }, [notes, project]);

  useEffect(() => {
    if (!highlightNoteId || !highlightRef.current) return;
    highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightNoteId, displayNotes]);

  const highlightedNote = useMemo(
    () =>
      highlightNoteId == null
        ? null
        : notes.find((note) => Number(note.id) === Number(highlightNoteId)) ?? null,
    [highlightNoteId, notes],
  );
  const replyAuthorLogin = (
    replyDraft?.authorLogin ||
    highlightedNote?.created_by_login ||
    ""
  ).trim();
  const replyAuthorDisplay = (
    replyDraft?.authorDisplay ||
    highlightedNote?.created_by_display ||
    ""
  ).trim();
  const replySignature = replyDraft
    ? `${replyDraft.key}|${replyAuthorLogin}|${replyAuthorDisplay}`
    : "";

  useEffect(() => {
    if (!replyDraft || !replySignature) {
      setMentionSeed(null);
      setReplyHint(null);
      return;
    }
    let cancelled = false;
    setReplyHint(null);
    void resolveReplyEmployee({
      login: replyAuthorLogin,
      display: replyAuthorDisplay,
    })
      .then((employee) => {
        if (cancelled) return;
        const seed = employee
          ? buildEmployeeMentionSeed(employee, `${replyDraft.key}:${employee.id}`)
          : null;
        if (!seed) {
          setMentionSeed(null);
          setReplyHint(
            "Die Person konnte nicht eindeutig zugeordnet werden. Bitte mit @ erwähnen, damit die Antwort ankommt.",
          );
          return;
        }
        setReplyHint(null);
        setMentionSeed(seed);
      })
      .catch(() => {
        if (cancelled) return;
        setMentionSeed(null);
        setReplyHint(
          "Die Erwähnung konnte nicht geladen werden. Bitte die Person mit @ auswählen.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [replyDraft, replySignature, replyAuthorLogin, replyAuthorDisplay]);

  function noteOwnedByUser(note: ProjectNote): boolean {
    const author = String(note.created_by_login || "").toLowerCase();
    if (!author || !login) return false;
    return author === login.toLowerCase() || author.startsWith(`${login.toLowerCase()}@`);
  }

  async function handleSubmit(payload: {
    note: string;
    visibility: "all" | "buero";
    references: DraftNoteReference[];
  }) {
    setSaving(true);
    setError(null);
    try {
      await postOrderNote(orderId, {
        note: payload.note,
        visibility: payload.visibility,
        references: payload.references.map((r) => ({
          type: r.type,
          ref_id: r.ref_id,
          label: r.label,
          trigger: r.trigger,
          token_text: r.token_text,
          start: r.start,
          end: r.end,
        })),
      });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(noteId: number | string) {
    if (!window.confirm("Notiz wirklich löschen?")) return;
    try {
      await deleteOrderNote(orderId, noteId);
      notifyMentionsBadgeRefresh();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen");
    }
  }

  return (
    <OverviewCard title="Notizen" fill className="eh-po-chat-panel">
      <div className="eh-po-chat-thread eh-po-notes-list">
        {!displayNotes.length ? (
          <p className="eh-po-chat-empty">Noch keine Notizen.</p>
        ) : (
          displayNotes.map((note) => {
            const own = !note._system && noteOwnedByUser(note);
            const isHighlighted =
              highlightNoteId != null && Number(note.id) === Number(highlightNoteId);
            const systemEvent = note._system ? null : parseSystemNote(note.note);
            if (note._system) {
              return (
                <p key={String(note.id)} className="eh-po-note-marker">
                  {note.note}
                </p>
              );
            }
            if (systemEvent) {
              return (
                <SystemNoteCard
                  key={String(note.id)}
                  note={note}
                  event={systemEvent}
                  own={own}
                  highlighted={isHighlighted}
                  cardRef={isHighlighted ? highlightRef : undefined}
                  onDelete={() => void handleDelete(note.id)}
                  onRefClick={(type, id, label) => void handleRefClick(type, id, label)}
                />
              );
            }
            return (
              <article
                key={String(note.id)}
                ref={isHighlighted ? highlightRef : undefined}
                className={clsx(
                  "eh-po-chat-bubble",
                  note._system && "eh-po-chat-bubble--system",
                  own && "eh-po-chat-bubble--own",
                  note.visibility === "buero" && "eh-po-chat-bubble--buero",
                  note._pending && "opacity-70",
                  isHighlighted && "eh-po-chat-bubble--highlight",
                )}
              >
                <header className="eh-po-chat-bubble-meta">
                  <span className="eh-po-chat-author">
                    {note.created_by_display || note.created_by_login || "Unbekannt"}
                  </span>
                  {note.created_at ? (
                    <time className="eh-po-chat-time" dateTime={note.created_at}>
                      {fmtNoteTime(note.created_at)}
                    </time>
                  ) : null}
                  {note.visibility === "buero" ? (
                    <span className="eh-po-chat-badge">Nur Büro</span>
                  ) : null}
                  {note.references?.some((r) => r.type === "material_anforderung") ? (
                    <span className="eh-po-chat-badge">Material</span>
                  ) : null}
                  {note._pending ? <span className="eh-po-chat-badge">Senden …</span> : null}
                  {own && !note._pending ? (
                    <button
                      type="button"
                      className="eh-po-chat-delete"
                      title="Eigene Notiz löschen"
                      aria-label="Eigene Notiz löschen"
                      onClick={() => void handleDelete(note.id)}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden>
                        <path
                          fill="currentColor"
                          d="M9 3h6l1 2h4v2H4V5h4l1-2zm-2 6h2v9H7V9zm4 0h2v9h-2V9zm4 0h2v9h-2V9zM6 7h12l-1 14H7L6 7z"
                        />
                      </svg>
                    </button>
                  ) : null}
                </header>
                <div className="eh-po-chat-bubble-body">
                  <NoteMessageBody
                    note={note}
                    onRefClick={(type, id, label) => void handleRefClick(type, id, label)}
                  />
                </div>
              </article>
            );
          })
        )}
      </div>

      {allowCompose ? (
        <>
          {replyHint ? (
            <p className="mb-2 text-xs text-label-secondary">{replyHint}</p>
          ) : null}
          <NoteComposer
            orderId={orderId}
            isBuero={isBuero}
            saving={saving}
            error={error}
            mentionSeed={mentionSeed}
            autoFocus={Boolean(replyDraft)}
            placeholder={replyDraft ? "Antwort schreiben …" : "Nachricht eingeben …"}
            onSubmit={(payload) => void handleSubmit(payload)}
          />
        </>
      ) : (
        <p className="mt-2 text-xs text-label-tertiary">
          Projektnotizen können nach „Bearbeitung übernehmen“ geschrieben werden.
        </p>
      )}

      {fallbackModal}
    </OverviewCard>
  );
}
